import { registerCellType } from 'handsontable/cellTypes';
import { TextEditor } from 'handsontable/editors';
import type Handsontable from 'handsontable';
import { baseRenderer } from 'handsontable/renderers';

import { Icons } from '@/constants/Icons';
import { _notNil, _isNotEmpty } from '@/littledash';
import type { Color } from '@/model/Common.model';
import iro from '@jaames/iro';

import './ColorEditor.scss';

const validHexColorPattern = /^#([0-9A-F]{3}){1,2}$/i;

/**
 * Color cell renderer
 */
export const colorRenderer = (
  hotInstance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  prop: string | number,
  value: string | null | undefined,
  cellProperties: Handsontable.CellProperties
): void => {
  baseRenderer(hotInstance, td, row, column, prop, value, cellProperties);

  // Clear cell content
  while (td.firstChild) {
    td.removeChild(td.firstChild);
  }

  if (!td.classList.contains('htInvalid') && typeof value === 'string' && _isNotEmpty(value)) {
    const colorChip = document.createElement('span');
    colorChip.classList.add('color-render-chip');
    colorChip.style.backgroundColor = value;
    td.appendChild(colorChip);
  }
};

/**
 * Color cell validator
 */
export const colorValidator = (value: unknown, callback: (valid: boolean) => void): void => {
  callback(typeof value === 'string' && validHexColorPattern.test(value));
};

/**
 * Color swatch palette
 */
const colorSwatch: Array<Color> = [
  '#333333',
  '#0066CC',
  '#B90745',
  '#F39521',
  '#743789',
  '#4EACF6',
  '#E56C92',
  '#78AD2B',
  '#F6CB45',
  '#00968C',
  '#8E1807',
  '#00007D',
  '#9B6D31',
  '#FF475A',
  '#0BCAB5',
  '#D4E27E',
  '#3E4EE0',
  '#EAC76C',
  '#FFCB90',
  '#90DED6',
  '#E5BFEE',
  '#879CA0',
  '#5F44AD',
  '#C9E4EB',
];

export class ColorEditor extends TextEditor {
  static type = 'color';

  // Private variables for compatibility
  private _colorValue?: string;

  // UI elements (created in createElements)
  colorPickerContainer?: HTMLDivElement;
  colorPickerModal?: HTMLDivElement;
  colorPickerHeader?: HTMLDivElement;
  colorPickerBody?: HTMLDivElement;
  colorPickerFooter?: HTMLDivElement;
  iconSvg?: SVGSVGElement;
  iconPath?: SVGPathElement;
  colorHexInput?: HTMLInputElement;
  colorChip?: HTMLSpanElement;
  saveButton?: HTMLButtonElement;
  cancelButton?: HTMLButtonElement;
  colorPicker?: iro.ColorPicker;

  /**
   * Register the color cell type
   */
  static register(type = ColorEditor.type): void {
    registerCellType(type, {
      editor: ColorEditor,
      renderer: colorRenderer,
      validator: colorValidator,
    });
  }

  /**
   * Color palette
   */
  static swatch: Array<Color> = colorSwatch;

  static randomColor(): Color {
    return `#${((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0')}`;
  }

  static randomSwatchColor(): Color {
    return ColorEditor.swatch[Math.floor(Math.random() * ColorEditor.swatch.length)];
  }

  static colorForIndex(index: number): Color {
    if (_notNil(ColorEditor.swatch?.[index])) {
      return ColorEditor.swatch[index];
    }
    return ColorEditor.randomColor();
  }

  /**
   * Initialize editor - called once per table instance
   */
  init(): void {
    super.init();
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  /**
   * Prepare editor - override WITHOUT calling super for compatibility
   */
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: Handsontable.CellProperties
  ): void {
    // Manually set all properties (don't call super.prepare)
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.cellProperties = cellProperties;
    this.originalValue = originalValue;

    // Store color value in private variable
    if (
      _notNil(originalValue) &&
      typeof originalValue === 'string' &&
      _isNotEmpty(originalValue) &&
      validHexColorPattern.test(originalValue)
    ) {
      this._colorValue = originalValue;
    } else {
      this._colorValue = ColorEditor.colorForIndex(row);
    }
  }

  /**
   * Create DOM elements for the color picker
   * Called automatically by the TextEditor constructor
   */
  createElements(): void {
    super.createElements();

    // Guard against double creation (can happen with updateSettings)
    if (_notNil(this.colorPickerContainer)) {
      return;
    }

    // Clean up any orphaned containers from previous instances
    const existingContainer = this.hot.rootDocument.querySelector('.color-picker-container');
    if (_notNil(existingContainer)) {
      existingContainer.remove();
    }

    // Main container
    this.colorPickerContainer = this.hot.rootDocument.createElement('div');
    this.colorPickerContainer.classList.add('color-picker-container');
    this.colorPickerContainer.style.display = 'none';
    this.colorPickerContainer.setAttribute('data-test-component', 'ColorEditor');
    this.colorPickerContainer.setAttribute('data-test-element', 'container');
    this.colorPickerContainer.setAttribute('data-test-id', 'color-picker-container');

    // Modal
    this.colorPickerModal = this.hot.rootDocument.createElement('div');
    this.colorPickerModal.classList.add('color-picker-modal');
    this.colorPickerModal.style.top = '0';
    this.colorPickerModal.style.left = '0';
    this.colorPickerContainer.appendChild(this.colorPickerModal);

    // Header
    this.colorPickerHeader = this.hot.rootDocument.createElement('div');
    this.colorPickerHeader.classList.add('color-picker-header');
    this.colorPickerModal.appendChild(this.colorPickerHeader);

    // Body
    this.colorPickerBody = this.hot.rootDocument.createElement('div');
    this.colorPickerBody.classList.add('color-picker-body');
    this.colorPickerModal.appendChild(this.colorPickerBody);

    // Footer
    this.colorPickerFooter = this.hot.rootDocument.createElement('div');
    this.colorPickerFooter.classList.add('color-picker-footer');
    this.colorPickerModal.appendChild(this.colorPickerFooter);

    // Pencil icon
    this.iconSvg = this.hot.rootDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.iconSvg.setAttribute('viewBox', '0 0 32 32');
    this.iconSvg.setAttribute('height', '24');
    this.iconSvg.setAttribute('width', '24');
    this.iconSvg.classList.add('icon', 'pencil-icon');

    this.iconPath = this.hot.rootDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.iconPath.setAttribute('d', Icons['pencil']);
    this.iconSvg.appendChild(this.iconPath);
    this.colorPickerHeader.appendChild(this.iconSvg);

    // Hex input
    this.colorHexInput = this.hot.rootDocument.createElement('input');
    this.colorHexInput.classList.add('color-picker-hex-input');
    this.colorHexInput.maxLength = 7;
    this.colorHexInput.setAttribute('data-test-element', 'hex-input');
    this.colorPickerHeader.appendChild(this.colorHexInput);

    // Color chip preview
    this.colorChip = this.hot.rootDocument.createElement('span');
    this.colorChip.classList.add('color-picker-chip');
    this.colorPickerHeader.appendChild(this.colorChip);

    // Save button
    this.saveButton = this.hot.rootDocument.createElement('button');
    this.saveButton.classList.add('color-picker-save');
    this.saveButton.innerText = 'Save';
    this.saveButton.setAttribute('data-test-element', 'save-button');
    this.colorPickerFooter.appendChild(this.saveButton);

    // Cancel button
    this.cancelButton = this.hot.rootDocument.createElement('button');
    this.cancelButton.classList.add('color-picker-cancel', 'plain', 'ml2');
    this.cancelButton.innerText = 'Cancel';
    this.colorPickerFooter.appendChild(this.cancelButton);

    // Append to body
    this.hot.rootDocument.body.appendChild(this.colorPickerContainer);

    // Event listeners
    this.colorPickerModal.addEventListener('mousedown', (e) => e.stopPropagation());
    this.colorPickerContainer.addEventListener('mousedown', () => this.finishEditing(false));

    // Initialize iro color picker
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.colorPicker = new (iro.ColorPicker as any)(this.colorPickerBody!, {
      color: '#ffffff',
      layoutDirection: 'horizontal',
      width: 200,
      layout: [{ component: iro.ui.Box }, { component: iro.ui.Slider, options: { sliderType: 'hue' } }],
    });

    if (_notNil(this.colorPicker)) {
      this.colorPicker.on('color:change', (color: iro.Color) => this.setValue(color.hexString));
    }

    this.colorHexInput!.addEventListener('input', (event) => {
      const value = (event.target as HTMLInputElement)?.value ?? '';
      if (validHexColorPattern.test(value)) {
        this.setValue(value);
      } else if (!value.startsWith('#')) {
        this.setValue(`#${value}`);
      }
    });

    this.saveButton!.addEventListener('click', () => this.finishEditing(false));
    this.cancelButton!.addEventListener('click', () => this.finishEditing(true));
    this.colorChip!.addEventListener('click', () => this.setValue(ColorEditor.randomSwatchColor()));
  }

  /**
   * Set editor value and sync all UI elements
   */
  setValue(newValue: string): void {
    const color = newValue ?? ColorEditor.randomSwatchColor();

    super.setValue(color);

    if (_notNil(this.colorPicker) && this.colorPicker.color.hexString !== color) {
      this.colorPicker.color.set(color);
    }

    if (_notNil(this.colorHexInput) && this.colorHexInput.value !== color) {
      this.colorHexInput.value = color;
    }

    if (_notNil(this.TEXTAREA) && this.TEXTAREA instanceof HTMLTextAreaElement) {
      if (this.TEXTAREA.value !== color) {
        this.TEXTAREA.value = color;
        this.TEXTAREA.style.color = color;
        this.TEXTAREA.style.backgroundColor = color;
      }
    }

    if (_notNil(this.colorChip)) {
      this.colorChip.style.backgroundColor = color;
    }
  }

  /**
   * Get current editor value
   */
  getValue(): string {
    // Return empty string if elements not created yet
    if (!_notNil(this.colorHexInput)) {
      return '';
    }
    return this.colorHexInput.value ?? '';
  }

  /**
   * Clean up DOM elements
   */
  destroyElements(): void {
    if (_notNil(this.colorPickerContainer)) {
      while (this.colorPickerContainer.firstChild) {
        this.colorPickerContainer.removeChild(this.colorPickerContainer.firstChild);
      }
      this.colorPickerContainer.remove();
      this.colorPickerContainer = undefined;
    }
  }

  /**
   * Finish editing with optional value restoration
   */
  finishEditing(restoreOriginalValue = false, ctrlDown = false): void {
    if (restoreOriginalValue && _notNil(this.originalValue)) {
      this.setValue(this.originalValue as string);
    }
    this.close();
    super.finishEditing(restoreOriginalValue, ctrlDown);
  }

  /**
   * Open editor - show color picker
   * Defensive check ensures elements exist (handles updateSettings recreation)
   */
  open(event: Event | null = null): void {
    // Defensive: ensure elements exist (handles updateSettings recreation)
    if (!_notNil(this.colorPickerContainer)) {
      this.createElements();
    }

    // Don't call super.open() to avoid internal issues
    this.showColorPicker(event);
  }

  /**
   * Close editor - hide color picker
   */
  close(): void {
    // Don't call super.close() to avoid internal issues
    this.hideColorPicker();
  }

  /**
   * Focus the hex input
   */
  focus(): void {
    // Don't call super.focus() - focus our custom input directly
    this.colorHexInput?.focus();
  }

  /**
   * Show color picker modal
   */
  showColorPicker(_event: Event | null): void {
    const { top = 0, left = 0 } = this.TD?.getBoundingClientRect() ?? {};

    if (_notNil(this.colorPickerModal)) {
      this.colorPickerModal.style.top = `${top}px`;
      this.colorPickerModal.style.left = `${left}px`;
    }

    const valueToSet = this._colorValue ?? (this.originalValue as string);
    this.setValue(valueToSet);

    if (_notNil(this.colorPickerContainer)) {
      this.colorPickerContainer.style.display = 'block';
    }
  }

  /**
   * Hide color picker modal
   */
  hideColorPicker(): void {
    if (_notNil(this.colorPickerContainer)) {
      this.colorPickerContainer.style.display = 'none';
    }
  }
}
